import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchSliders,
  createSliderThunk,
  updateSliderThunk,
  deleteSliderThunk,
} from "../../features/auth/store/sliderSlice";
import { SliderList } from "./component";
import { ModalContainer } from "../../components/PageComponents";
import { OutlineButton, GradientButton } from "../../components/forms";
import { SliderItem } from "../../features/auth/store/types/slider-types";

const SlidersManagementPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const items: SliderItem[] = useSelector(
    (state: any) => state.sliders?.items ?? [],
  );
  const loading: boolean = useSelector(
    (state: any) => state.sliders?.loading ?? false,
  );
  const saving: boolean = useSelector(
    (state: any) => state.sliders?.saving ?? false,
  );
  const error: string | null = useSelector(
    (state: any) => state.sliders?.error ?? null,
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SliderItem | null>(null);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [desktopFile, setDesktopFile] = useState<File | null>(null);
  const [mobileFile, setMobileFile] = useState<File | null>(null);

  useEffect(() => {
    dispatch(fetchSliders());
  }, [dispatch]);

  useEffect(() => {
    if (editing) {
      setTitle(editing.title ?? "");
      setUrl(editing.url ?? "");
      setDesktopFile(null);
      setMobileFile(null);
    } else {
      setTitle("");
      setUrl("");
      setDesktopFile(null);
      setMobileFile(null);
    }
  }, [editing, formOpen]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (item: SliderItem) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleDelete = (item: SliderItem) => {
    if (!window.confirm("Delete this slider?")) return;
    dispatch(deleteSliderThunk(item.id));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (editing) {
      dispatch(
        updateSliderThunk({
          id: editing.id,
          title,
          url,
          desktop_image: desktopFile ?? null,
          mobile_image: mobileFile ?? null,
        }),
      );
    } else {
      dispatch(
        createSliderThunk({
          title,
          url,
          desktop_image: desktopFile ?? null,
          mobile_image: mobileFile ?? null,
        }),
      );
    }
    setFormOpen(false);
  };

  const CloseIcon = () => {
    return (
      <button
        onClick={() => setFormOpen(false)}
        className="text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-white transition-colors"
        type="button"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    );
  };

  return (
    <div className="p-4 sm:p-6 dark:bg-zinc-950 min-h-screen">
      {/* Header Container */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold dark:text-white">Sliders</h2>
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            Manage your homepage application sliders, banners, and routing
            targets.
          </p>
        </div>

        {/* Actions Row */}
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <OutlineButton onClick={openCreate} content="New Slider" />
          <GradientButton
            onClick={() => dispatch(fetchSliders())}
            content="Refresh"
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <SliderList items={items} onEdit={openEdit} onDelete={handleDelete} />
        )}
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 mt-2">
            {error}
          </div>
        )}
      </div>

      {/* Slide Edit/Creation Management Modal Form Context */}
      {formOpen && (
        <ModalContainer
          title={editing ? "Edit Slider" : "Create Slider"}
          closeIcon={<CloseIcon />}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Target URL
              </label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white"
                placeholder="e.g. /referrals"
              />
            </div>

            {/* Large / Medium Desktop File Upload Element Container */}
            <div>
              <label className="block mb-2 text-xs font-semibold dark:text-zinc-300">
                Large / Medium Image (Desktop)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-white">
                <input
                  type="file"
                  id="desktopImageUpload"
                  accept="image/*"
                  onChange={(e) => setDesktopFile(e.target.files?.[0] ?? null)}
                  className="hidden"
                />
                <label
                  htmlFor="desktopImageUpload"
                  className="flex flex-col items-center justify-center cursor-pointer w-full"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="mb-2 text-gray-400 dark:text-zinc-500"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 8L12 3L7 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 3V15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-xs text-gray-600 dark:text-zinc-400 break-words max-w-full block px-2">
                    {desktopFile ? desktopFile.name : "Upload Desktop Banner"}
                  </span>
                </label>
              </div>

              {/* Desktop Image Preview Block */}
              {(desktopFile || editing?.desktop_image_url) && (
                <div className="mt-3 flex gap-4">
                  <div className="relative rounded-md overflow-hidden h-24 w-44 ring-1 ring-gray-200 dark:ring-zinc-700">
                    <img
                      src={
                        desktopFile
                          ? URL.createObjectURL(desktopFile)
                          : editing?.desktop_image_url || ""
                      }
                      alt="desktop preview"
                      className="w-full h-full object-cover"
                    />
                    {desktopFile && (
                      <button
                        type="button"
                        onClick={() => setDesktopFile(null)}
                        className="absolute top-1 right-1 bg-white dark:bg-zinc-900 rounded-full p-1 shadow"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#E53E3E"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Small Screen Mobile Image File Upload Element Container */}
            <div>
              <label className="block mb-2 text-xs font-semibold dark:text-zinc-300">
                Small Screen Image (Mobile)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-white">
                <input
                  type="file"
                  id="mobileImageUpload"
                  accept="image/*"
                  onChange={(e) => setMobileFile(e.target.files?.[0] ?? null)}
                  className="hidden"
                />
                <label
                  htmlFor="mobileImageUpload"
                  className="flex flex-col items-center justify-center cursor-pointer w-full"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="mb-2 text-gray-400 dark:text-zinc-500"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 8L12 3L7 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 3V15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-xs text-gray-600 dark:text-zinc-400 break-words max-w-full block px-2">
                    {mobileFile ? mobileFile.name : "Upload Mobile Banner"}
                  </span>
                </label>
              </div>

              {/* Mobile Image Preview Block */}
              {(mobileFile || editing?.mobile_image_url) && (
                <div className="mt-3 flex gap-4">
                  <div className="relative rounded-md overflow-hidden h-24 w-24 ring-1 ring-gray-200 dark:ring-zinc-700">
                    <img
                      src={
                        mobileFile
                          ? URL.createObjectURL(mobileFile)
                          : editing?.mobile_image_url || ""
                      }
                      alt="mobile preview"
                      className="w-full h-full object-cover"
                    />
                    {mobileFile && (
                      <button
                        type="button"
                        onClick={() => setMobileFile(null)}
                        className="absolute top-1 right-1 bg-white dark:bg-zinc-900 rounded-full p-1 shadow"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#E53E3E"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Form Submission Navigation Panel */}
            <div className="flex gap-2 justify-end pt-2">
              <OutlineButton
                onClick={() => setFormOpen(false)}
                content="Cancel"
              />
              <GradientButton
                type="submit"
                disabled={saving}
                content={editing ? "Update" : "Create"}
              />
            </div>
          </form>
        </ModalContainer>
      )}
    </div>
  );
};

export default SlidersManagementPage;
